import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { TimeInput } from '@/components/ui/TimeInput';
import { TYPE } from '@/lib/typography';
import {
  clinicDayHoursKind,
  defaultOpenPeriods,
  dkWeekdayKeyFromDate,
  formatHourPeriods,
  getEffectiveBookableWindows,
  normalizeHourPeriods,
  resolveClinicDayHours,
  type ClinicHoursException,
  type HourPeriod,
  type MemberDayException,
  type WeeklyHoursShape,
} from '@/lib/clinicHours';
import { useCountAppointmentsOnDate } from '@/hooks/useClinicHoursExceptions';
import { useUpsertPractitionerDayHours } from '@/hooks/usePractitionerDayHours';
import { formatAppointmentDateHeading } from '@/utils/appointmentTimeFormat';

export function ClinicDayHoursModal({
  clinicId,
  date,
  isoDate,
  weeklyHours,
  exception,
  practitionerIds,
  practitionerNames,
  practitionerWeekly,
  memberExceptions,
  canEdit,
  onClose,
}: {
  clinicId: string;
  date: Date;
  isoDate: string;
  weeklyHours: WeeklyHoursShape;
  exception: ClinicHoursException | null;
  practitionerIds: string[];
  practitionerNames: string[];
  practitionerWeekly?: { enabled: boolean; start: string; end: string } | null;
  memberExceptions?: MemberDayException[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const upsert = useUpsertPractitionerDayHours(clinicId);
  const appointmentCount = useCountAppointmentsOnDate(clinicId, isoDate);

  const weekdayKey = dkWeekdayKeyFromDate(date);
  const weekly = weeklyHours[weekdayKey] ?? null;
  const resolved = resolveClinicDayHours({ weekly, exception });
  const kind = clinicDayHoursKind(weekly, resolved);

  const memberCustom = (memberExceptions ?? []).find((item) => item.type === 'custom_hours');
  const memberClosed = (memberExceptions ?? []).some(
    (item) =>
      (item.type === 'unavailable' || item.type === 'vacation' || item.type === 'sick') &&
      !item.startsTime,
  );
  const effective = getEffectiveBookableWindows({
    clinicPeriods: resolved.periods,
    clinicSource: resolved.source,
    clinicExceptionType: resolved.exceptionType,
    clinicianDay: practitionerWeekly,
    memberExceptions,
  });

  const persistedSignature = [
    isoDate,
    weekly?.enabled ? '1' : '0',
    weekly?.start ?? '',
    weekly?.end ?? '',
    practitionerWeekly?.enabled ? '1' : '0',
    practitionerWeekly?.start ?? '',
    practitionerWeekly?.end ?? '',
    ...(memberExceptions ?? [])
      .map((item) => `${item.type}:${item.startsTime ?? ''}:${item.endsTime ?? ''}`)
      .sort(),
  ].join('|');

  const [mode, setMode] = useState<'weekly' | 'closed' | 'custom'>(
    memberClosed ? 'closed' : memberCustom ? 'custom' : 'weekly',
  );
  const [periods, setPeriods] = useState<HourPeriod[]>(
    memberCustom?.startsTime && memberCustom.endsTime
      ? [{ start: memberCustom.startsTime, end: memberCustom.endsTime }]
      : effective.length > 0
        ? effective
        : defaultOpenPeriods(weeklyHours),
  );

  useEffect(() => {
    const custom = (memberExceptions ?? []).find((item) => item.type === 'custom_hours');
    const closed = (memberExceptions ?? []).some(
      (item) =>
        (item.type === 'unavailable' || item.type === 'vacation' || item.type === 'sick') &&
        !item.startsTime,
    );
    setMode(closed ? 'closed' : custom ? 'custom' : 'weekly');
    setPeriods(
      custom?.startsTime && custom.endsTime
        ? [{ start: custom.startsTime, end: custom.endsTime }]
        : practitionerWeekly?.enabled && practitionerWeekly.start && practitionerWeekly.end
          ? [{ start: practitionerWeekly.start, end: practitionerWeekly.end }]
          : defaultOpenPeriods(weeklyHours),
    );
    // Only re-sync when persisted values change. Parent arrays are new every
    // render and must not snap "Rediger åbningstider" back to weekly.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signature covers inputs
  }, [persistedSignature]);

  const locale = i18n.language === 'da' ? 'da-DK' : 'en-US';
  const dateLabel = useMemo(() => formatAppointmentDateHeading(date, locale), [date, locale]);

  const weeklyLabel = practitionerWeekly?.enabled
    ? formatHourPeriods([{ start: practitionerWeekly.start, end: practitionerWeekly.end }])
    : weekly?.enabled
      ? formatHourPeriods([{ start: weekly.start, end: weekly.end }])
      : t('settings.hours.closed');
  const targetLabel =
    practitionerNames.length > 0
      ? practitionerNames.join(', ')
      : t('appointments.day_hours.selected_practitioners_missing');

  const statusLabel = {
    normal_open: t('appointments.day_hours.status_normal_open'),
    normal_closed: t('appointments.day_hours.status_normal_closed'),
    exception_open: t('appointments.day_hours.status_exception_open'),
    exception_closed: t('appointments.day_hours.status_exception_closed'),
    exception_custom: t('appointments.day_hours.status_exception_custom'),
  }[kind];

  const existingAppointments = appointmentCount.data ?? 0;
  const showCloseWarning = mode === 'closed' && existingAppointments > 0;
  const pending = upsert.isPending;

  function updatePeriod(index: number, patch: Partial<HourPeriod>) {
    setPeriods((current) => current.map((period, i) => (i === index ? { ...period, ...patch } : period)));
  }

  async function save() {
    try {
      if (practitionerIds.length === 0) {
        toast.error(t('appointments.day_hours.selected_practitioners_missing'));
        return;
      }
      if (mode === 'weekly') {
        await upsert.mutateAsync({
          practitionerIds,
          date: isoDate,
          action: 'weekly',
        });
        toast.success(t('appointments.day_hours.restored_toast'));
        onClose();
        return;
      }
      if (mode === 'closed') {
        if (showCloseWarning && !window.confirm(t('appointments.day_hours.close_with_appointments_confirm', { count: existingAppointments }))) {
          return;
        }
        await upsert.mutateAsync({
          practitionerIds,
          date: isoDate,
          action: 'closed',
        });
        toast.success(t('appointments.day_hours.closed_toast'));
        onClose();
        return;
      }
      const normalized = normalizeHourPeriods(periods.slice(0, 1));
      await upsert.mutateAsync({
        practitionerIds,
        date: isoDate,
        action: 'custom_hours',
        start: normalized[0]?.start,
        end: normalized[0]?.end,
      });
      toast.success(t('appointments.day_hours.saved_toast'));
      onClose();
    } catch (err) {
      console.error('clinic hours exception failed', err);
      toast.error(t('appointments.day_hours.save_error'));
    }
  }

  return (
    <Modal title={t('appointments.day_hours.title')} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <div className={`${TYPE.section} normal-case text-ds-text`}>{dateLabel}</div>
          <p className={`${TYPE.small} mt-1 text-ds-muted`}>{statusLabel}</p>
          <p className={`${TYPE.small} mt-1 text-ds-text`}>
            {t('appointments.day_hours.applies_to', { names: targetLabel })}
          </p>
        </div>

        <div className="rounded-lg bg-ds-sunk p-3">
          <div className={`${TYPE.small} text-ds-muted`}>
            {t('appointments.day_hours.normal_hours')}
          </div>
          <div className={`${TYPE.body} font-medium text-ds-text`}>{weeklyLabel}</div>
        </div>

        {canEdit ? (
          <fieldset className="space-y-2">
            <legend className={`${TYPE.label} text-ds-text`}>
              {t('appointments.day_hours.this_date')}
            </legend>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="day-hours-mode"
                checked={mode === 'weekly'}
                onChange={() => setMode('weekly')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>{t('appointments.day_hours.use_normal')}</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="day-hours-mode"
                checked={mode === 'closed'}
                onChange={() => setMode('closed')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>{t('appointments.day_hours.close_day')}</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="day-hours-mode"
                checked={mode === 'custom'}
                onChange={() => setMode('custom')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>
                {weekly?.enabled
                  ? t('appointments.day_hours.change_hours')
                  : t('appointments.day_hours.open_exceptionally')}
              </span>
            </label>
          </fieldset>
        ) : (
          <p className={`${TYPE.small} text-ds-muted`}>{t('appointments.day_hours.read_only')}</p>
        )}

        {mode === 'custom' && canEdit && (
          <div className="space-y-2">
            {periods.map((period, index) => (
              <div key={`${period.start}-${index}`} className="flex flex-wrap items-center gap-2">
                <TimeInput
                  value={period.start}
                  onChange={(start) => updatePeriod(index, { start })}
                />
                <span className={`${TYPE.small} text-ds-muted`}>{t('settings.hours.to')}</span>
                <TimeInput
                  value={period.end}
                  onChange={(end) => updatePeriod(index, { end })}
                />
                {periods.length > 1 && (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => setPeriods((current) => current.filter((_, i) => i !== index))}
                    aria-label={t('appointments.day_hours.remove_period')}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
            ))}
            {periods.length < 8 && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPeriods((current) => [...current, { start: '13:00', end: '18:00' }])}
              >
                <Plus />
                {t('appointments.day_hours.add_period')}
              </Button>
            )}
          </div>
        )}

        {showCloseWarning && (
          <div className="flex gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3 text-ds-amber-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className={TYPE.small}>
              {t('appointments.day_hours.close_with_appointments', { count: existingAppointments })}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            {t('common.cancel', 'Annuller')}
          </Button>
          {canEdit && (
            <Button type="button" onClick={() => void save()} disabled={pending}>
              {t('common.save', 'Gem')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
